import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface SmtpConfig {
  host: string;
  port: number;
  user: string;
  pass: string;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  secure: boolean;
  updatedAt?: string;
}

export interface SmtpTestResult {
  success: boolean;
  message: string;
  details?: string;
  error?: string;
  timestamp: string;
}

export interface EmailLogEntry {
  id: string;
  recipient: string;
  subject: string;
  type: 'verification' | 'password_reset' | 'test' | 'marketing' | 'system';
  status: 'success' | 'failure' | 'simulated';
  details?: string;
  error?: string;
  timestamp: string;
}

// Safe JSON extractor helper
async function safeJsonParse(res: Response): Promise<any> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return {
      success: false,
      error: res.statusText || `Erro no servidor (HTTP ${res.status})`,
      rawText: text
    };
  }
}

// Fetch SMTP Configuration from backend API or Firestore fallback
export async function getSmtpConfig(): Promise<SmtpConfig> {
  try {
    let res = await fetch('/api/admin/smtp-config');
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/smtp-config');
    }
    if (res.ok) {
      const data = await safeJsonParse(res);
      if (data && data.config) {
        return data.config;
      }
    }
  } catch (err) {
    console.warn('[SMTP Config] API fetch warning, falling back to Firestore client SDK:', err);
  }

  // Fallback to client-side Firestore
  try {
    const configRef = doc(db, 'config', 'smtp');
    const snap = await getDoc(configRef);
    if (snap.exists()) {
      const data = snap.data();
      return {
        host: data.host || 'smtp.gmail.com',
        port: Number(data.port) || 587,
        user: data.user || 'egami.corporation@gmail.com',
        pass: data.pass || '',
        fromName: data.fromName || 'Do Japão pra sua Casa',
        fromEmail: data.fromEmail || data.user || 'contato@dojapao.co.jp',
        replyTo: data.replyTo || 'contato@dojapao.co.jp',
        secure: data.secure !== undefined ? Boolean(data.secure) : false,
        updatedAt: data.updatedAt
      };
    }
  } catch (err) {
    console.error('[SMTP Config] Error reading from Firestore:', err);
  }

  return {
    host: 'smtp.gmail.com',
    port: 587,
    user: 'egami.corporation@gmail.com',
    pass: '',
    fromName: 'Do Japão pra sua Casa',
    fromEmail: 'contato@dojapao.co.jp',
    replyTo: 'contato@dojapao.co.jp',
    secure: false
  };
}

// Save SMTP Configuration via API and Firestore
export async function saveSmtpConfig(config: SmtpConfig): Promise<{ success: boolean; message: string }> {
  const payload = {
    ...config,
    port: Number(config.port),
    updatedAt: new Date().toISOString()
  };

  try {
    // 1. Save via Backend API
    let res = await fetch('/api/admin/smtp-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/smtp-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    if (res.ok) {
      const data = await safeJsonParse(res);
      return { success: true, message: data.message || 'Configurações de e-mail salvas com sucesso!' };
    }
  } catch (err) {
    console.warn('[SMTP Config] Backend API save failed, attempting Firestore direct save:', err);
  }

  // 2. Direct Firestore fallback
  try {
    const configRef = doc(db, 'config', 'smtp');
    await setDoc(configRef, payload, { merge: true });
    return { success: true, message: 'Configurações salvas diretamente no banco de dados (Firestore)!' };
  } catch (err: any) {
    console.error('[SMTP Config] Failed to save SMTP config:', err);
    return { success: false, message: err?.message || 'Erro ao salvar configurações de e-mail.' };
  }
}

// Test SMTP Connection and send a test email
export async function testSmtpConnection(config: SmtpConfig, testEmail: string): Promise<SmtpTestResult> {
  try {
    let res = await fetch('/api/admin/test-smtp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        smtpConfig: config,
        testEmail: testEmail.trim()
      })
    });

    if (!res.ok && res.status === 404) {
      res = await fetch('/api/test-smtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smtpConfig: config,
          testEmail: testEmail.trim()
        })
      });
    }

    const data = await safeJsonParse(res);

    if (res.ok && data.success) {
      return {
        success: true,
        message: data.message || `E-mail de teste enviado com sucesso para ${testEmail}!`,
        details: data.details,
        timestamp: new Date().toISOString()
      };
    } else {
      return {
        success: false,
        message: data.error || 'Falha ao testar conexão SMTP.',
        details: data.details,
        error: data.error,
        timestamp: new Date().toISOString()
      };
    }
  } catch (err: any) {
    return {
      success: false,
      message: 'Não foi possível conectar ao servidor backend para testar o SMTP.',
      error: err?.message || 'Erro de rede',
      timestamp: new Date().toISOString()
    };
  }
}

export interface AutomatedEmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  bodyHtml: string;
  enabled: boolean;
  variables: string[];
  updatedAt?: string;
}

// Fetch Automated System Email Templates
export async function getAutomatedEmailTemplates(): Promise<AutomatedEmailTemplate[]> {
  try {
    let res = await fetch('/api/admin/email-templates');
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/email-templates');
    }
    if (res.ok) {
      const data = await safeJsonParse(res);
      if (data && Array.isArray(data.templates)) {
        return data.templates;
      }
    }
  } catch (err) {
    console.warn('[Email Templates] Error fetching templates from API:', err);
  }
  return [];
}

// Save Automated System Email Templates
export async function saveAutomatedEmailTemplates(templates: AutomatedEmailTemplate[]): Promise<{ success: boolean; message?: string }> {
  try {
    let res = await fetch('/api/admin/email-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templates })
    });
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/email-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates })
      });
    }
    const data = await safeJsonParse(res);
    return { success: res.ok && data.success, message: data.message || data.error };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Erro ao conectar ao servidor para salvar templates.' };
  }
}

// Send Test Email for a specific Automated Template
export async function testAutomatedEmailTemplate(
  templateId: string, 
  testEmail: string, 
  sampleVars?: Record<string, string>
): Promise<{ success: boolean; message?: string; details?: string }> {
  try {
    let res = await fetch('/api/admin/test-automated-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId, testEmail, sampleVars })
    });
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/test-automated-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, testEmail, sampleVars })
      });
    }
    const data = await safeJsonParse(res);
    return { 
      success: res.ok && data.success, 
      message: data.message || data.error,
      details: data.details
    };
  } catch (err: any) {
    return { success: false, message: 'Erro ao testar envio do template.', details: err?.message };
  }
}

// Helper to trigger automated Welcome Email
export async function sendWelcomeEmail(targetEmail: string, userName: string, suiteNumber: string) {
  try {
    let res = await fetch('/api/admin/send-welcome-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, name: userName, suite: suiteNumber })
    });
    if (!res.ok && res.status === 404) {
      await fetch('/api/send-welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, name: userName, suite: suiteNumber })
      });
    }
  } catch (err) {
    console.warn('[Welcome Email] Send error:', err);
  }
}

// Helper to trigger Contact Confirmation Email
export async function sendContactConfirmationEmail(targetEmail: string, userName: string, subject: string, message: string) {
  try {
    let res = await fetch('/api/admin/send-contact-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, name: userName, subject, message })
    });
    if (!res.ok && res.status === 404) {
      await fetch('/api/send-contact-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, name: userName, subject, message })
      });
    }
  } catch (err) {
    console.warn('[Contact Email] Send error:', err);
  }
}

// Fetch Email Dispatch Logs from Backend API or Firestore
export async function getEmailLogs(): Promise<EmailLogEntry[]> {
  try {
    let res = await fetch('/api/admin/email-logs');
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/email-logs');
    }
    if (res.ok) {
      const data = await safeJsonParse(res);
      if (data && Array.isArray(data.logs)) {
        return data.logs;
      }
    }
  } catch (err) {
    console.warn('[Email Logs] API fetch error, checking Firestore:', err);
  }

  // Fallback to client-side Firestore
  try {
    const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
    const logsRef = collection(db, 'email_logs');
    const q = query(logsRef, orderBy('timestamp', 'desc'), limit(100));
    const snap = await getDocs(q);
    const logs: EmailLogEntry[] = [];
    snap.forEach((docSnap) => {
      logs.push({ id: docSnap.id, ...docSnap.data() } as EmailLogEntry);
    });
    return logs;
  } catch (err) {
    console.error('[Email Logs] Error fetching from Firestore:', err);
    return [];
  }
}

// Clear Email Dispatch Logs
export async function clearEmailLogs(): Promise<boolean> {
  try {
    let res = await fetch('/api/admin/email-logs/clear', { method: 'POST' });
    if (!res.ok && res.status === 404) {
      res = await fetch('/api/email-logs/clear', { method: 'POST' });
    }
    if (res.ok) {
      return true;
    }
  } catch (err) {
    console.warn('[Email Logs] Error clearing logs:', err);
  }
  return false;
}



export async function sendPackageReceivedEmail(targetEmail: string, userName: string, suiteNumber: string, packageTitle: string, trackingNumber: string, weight: string) {
  try {
    const res = await fetch('/api/admin/send-package-received-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, name: userName, suite: suiteNumber, packageTitle, trackingNumber, weight })
    });
    return await res.json();
  } catch (err) {
    console.error('Failed to send package received email:', err);
    return { success: false };
  }
}

export async function sendShipmentUpdateEmail(targetEmail: string, userName: string, suiteNumber: string, shipmentCode: string, status: string, carrier: string, trackingCode: string) {
  try {
    const res = await fetch('/api/admin/send-shipment-update-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, name: userName, suite: suiteNumber, shipmentCode, status, carrier, trackingCode })
    });
    return await res.json();
  } catch (err) {
    console.error('Failed to send shipment update email:', err);
    return { success: false };
  }
}

export async function sendConsolidationReadyEmail(targetEmail: string, userName: string, suiteNumber: string, boxCode: string, weight: string, cost: string) {
  try {
    const res = await fetch('/api/admin/send-consolidation-ready-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail, name: userName, suite: suiteNumber, boxCode, weight, cost })
    });
    return await res.json();
  } catch (err) {
    console.error('Failed to send consolidation ready email:', err);
    return { success: false };
  }
}
